import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, ChevronDown, Globe, User, LogOut, LayoutDashboard, Shield, Lock, Bug } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import ChatWidget from './ChatWidget';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2d045789-83c2-4d79-aa2c-4eeefb9b25ce/artifacts/n3gtt7ti_X%20Logo-01.png";

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { t, lang, changeLang, languages } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/domains', label: t('nav.domains') },
    { path: '/auctions', label: t('nav.auctions') },
  ];

  const serviceItems = [
    { path: '/valuation', label: t('services.valuation') },
    { path: '/domain-garden', label: t('services.garden') },
    { path: '/sell-domain', label: t('services.sell') },
    { path: '/brokerage', label: t('services.brokerage') },
    { path: '/rent-domain', label: t('services.rent') },
    { path: '/financing', label: 'Domain Financing' },
    { path: '/domain-donation', label: t('services.donate') },
    { path: '/blog', label: t('services.blog') },
    { path: '/affiliate', label: 'Affiliate Program' },
    { path: '/search', label: 'Advanced Search' },
    { path: '/marketplace-analytics', label: 'Market Analytics' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col bg-[#020202]">
      {/* Header */}
      <header className="glass-heavy sticky top-0 z-50" data-testid="main-header">
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
              <img src={LOGO_URL} alt="XDomainX" className="h-10 md:h-14 w-auto" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {navItems.map(item => (
                <Link key={item.path} to={item.path}
                  className={`px-4 py-2 text-sm font-medium tracking-wide uppercase transition-colors ${isActive(item.path) ? 'text-[#00E5FF]' : 'text-zinc-400 hover:text-white'}`}>
                  {item.label}
                </Link>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger className="px-4 py-2 text-sm font-medium tracking-wide uppercase text-zinc-400 hover:text-white flex items-center gap-1 transition-colors">
                  {t('nav.services')} <ChevronDown className="h-3 w-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#0A0A0A] border-white/10">
                  {serviceItems.map(item => (
                    <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)}
                      className="text-zinc-300 hover:text-[#00E5FF] focus:text-[#00E5FF] focus:bg-white/5 cursor-pointer">
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/contact"
                className={`px-4 py-2 text-sm font-medium tracking-wide uppercase transition-colors ${isActive('/contact') ? 'text-[#00E5FF]' : 'text-zinc-400 hover:text-white'}`}>
                {t('nav.contact')}
              </Link>
            </nav>

            {/* Right Side */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Language */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-600 rounded-sm transition-colors" data-testid="language-selector">
                  <Globe className="h-3.5 w-3.5" /> {lang.toUpperCase()}
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#0A0A0A] border-white/10">
                  {languages.map(l => (
                    <DropdownMenuItem key={l.code} onClick={() => changeLang(l.code)}
                      className={`cursor-pointer ${lang === l.code ? 'text-[#00E5FF]' : 'text-zinc-300'} hover:text-[#00E5FF] focus:text-[#00E5FF] focus:bg-white/5`}>
                      {l.flag} {l.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="text-zinc-300 hover:text-white gap-2" data-testid="user-menu-btn">
                      <User className="h-4 w-4" /> {user.name}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#0A0A0A] border-white/10">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} className="text-zinc-300 hover:text-[#00E5FF] focus:text-[#00E5FF] focus:bg-white/5 cursor-pointer">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> {t('nav.dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/security')} className="text-zinc-300 hover:text-[#00E5FF] focus:text-[#00E5FF] focus:bg-white/5 cursor-pointer">
                      <Lock className="h-4 w-4 mr-2" /> Security Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/bug-report')} className="text-zinc-300 hover:text-red-400 focus:text-red-400 focus:bg-white/5 cursor-pointer" data-testid="bug-report-link">
                      <Bug className="h-4 w-4 mr-2" /> Report a Bug
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="text-zinc-300 hover:text-[#00E5FF] focus:text-[#00E5FF] focus:bg-white/5 cursor-pointer">
                        <Shield className="h-4 w-4 mr-2" /> {t('nav.admin')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem onClick={logout} className="text-red-400 hover:text-red-300 focus:text-red-300 focus:bg-white/5 cursor-pointer">
                      <LogOut className="h-4 w-4 mr-2" /> {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" className="text-zinc-300 hover:text-white text-sm uppercase tracking-wider" data-testid="login-btn">
                      {t('nav.login')}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-[#00E5FF] text-black hover:bg-white font-bold text-sm uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)] hover:shadow-[0_0_30px_rgba(0,229,255,0.6)]" data-testid="register-btn">
                      {t('nav.register')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Toggle */}
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-zinc-400 hover:text-white p-2" data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden glass border-t border-white/5 animate-fade-in" data-testid="mobile-menu">
            <div className="px-6 py-4 space-y-2">
              {navItems.map(item => (
                <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                  className={`block py-2 text-sm font-medium uppercase tracking-wide ${isActive(item.path) ? 'text-[#00E5FF]' : 'text-zinc-400'}`}>
                  {item.label}
                </Link>
              ))}
              <div className="py-2 text-xs font-bold text-zinc-600 uppercase tracking-widest">{t('nav.services')}</div>
              {serviceItems.map(item => (
                <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                  className="block py-1.5 pl-3 text-sm text-zinc-400 hover:text-[#00E5FF]">{item.label}</Link>
              ))}
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-medium uppercase tracking-wide text-zinc-400">{t('nav.contact')}</Link>
              <div className="pt-3 flex gap-2">
                {languages.map(l => (
                  <button key={l.code} onClick={() => changeLang(l.code)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-sm border ${lang === l.code ? 'border-[#00E5FF] text-[#00E5FF]' : 'border-zinc-800 text-zinc-500'}`}>
                    {l.flag}
                  </button>
                ))}
              </div>
              {!user && (
                <div className="pt-3 flex gap-2">
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1">
                    <Button variant="outline" className="w-full border-zinc-800 text-zinc-300 rounded-sm">{t('nav.login')}</Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1">
                    <Button className="w-full bg-[#00E5FF] text-black font-bold rounded-sm">{t('nav.register')}</Button>
                  </Link>
                </div>
              )}
              {user && (
                <div className="pt-3 space-y-2">
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-zinc-400">{t('nav.dashboard')}</Link>
                  {isAdmin && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-zinc-400">{t('nav.admin')}</Link>}
                  <button onClick={() => { logout(); setMobileOpen(false); }} className="block py-2 text-sm text-red-400">{t('nav.logout')}</button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Floating Bug Report Button */}
      {user && (
        <Link to="/bug-report" className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-2.5 bg-zinc-900/90 border border-zinc-700 hover:border-red-500/50 text-zinc-400 hover:text-red-400 rounded-full shadow-lg backdrop-blur-sm transition-all duration-200 group" data-testid="floating-bug-report-btn">
          <Bug className="h-4 w-4" />
          <span className="text-xs font-medium tracking-wide uppercase">Report Bug</span>
        </Link>
      )}

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#020202]" data-testid="main-footer">
        <div className="px-6 md:px-12 max-w-7xl mx-auto py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-1">
              <Link to="/" className="inline-block">
                <img src={LOGO_URL} alt="XDomainX" className="h-16 w-auto mb-4" />
              </Link>
              <p className="text-xs text-zinc-600 uppercase tracking-widest">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4">Marketplace</h4>
              <div className="space-y-2">
                <Link to="/domains" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('nav.domains')}</Link>
                <Link to="/auctions" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('nav.auctions')}</Link>
                <Link to="/valuation" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('services.valuation')}</Link>
                <Link to="/sell-domain" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('services.sell')}</Link>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4">{t('nav.services')}</h4>
              <div className="space-y-2">
                <Link to="/domain-garden" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('services.garden')}</Link>
                <Link to="/brokerage" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('services.brokerage')}</Link>
                <Link to="/rent-domain" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('services.rent')}</Link>
                <Link to="/domain-donation" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('services.donate')}</Link>
                <Link to="/blog" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('services.blog')}</Link>
                <Link to="/affiliate" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">Affiliate Program</Link>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4">Partner Services</h4>
              <div className="space-y-2">
                <a href="https://markedhost.com/" target="_blank" rel="noopener noreferrer" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">Web Hosting</a>
                <a href="https://a-logo.com/" target="_blank" rel="noopener noreferrer" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">Logo & Design</a>
                <a href="https://webidea.co/" target="_blank" rel="noopener noreferrer" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">Website Development</a>
              </div>
              <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4 mt-6">Legal</h4>
              <div className="space-y-2">
                <Link to="/terms" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('footer.terms')}</Link>
                <Link to="/privacy" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('footer.privacy')}</Link>
                <Link to="/contact" className="block text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors">{t('nav.contact')}</Link>
              </div>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-xs text-zinc-600">&copy; 2026 XDomainX.com. {t('footer.rights')}</p>
              <p className="text-[10px] text-zinc-700 mt-1">Operated by Liber nostra d.o.o.</p>
            </div>
            <p className="text-xs text-zinc-700">info@xdomainx.com</p>
          </div>
        </div>
      </footer>

      <ChatWidget />
    </div>
  );
}
